import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTemplates } from "@/hooks/useTemplates";
import { useActiveProjectTemplates } from "@/hooks/useTemplates";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { projectsApi } from "@/services/projectsApi";
import { auditLogApi } from "@/services/auditLogApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";

interface TemplateSelectDialogProps {
  projectType: string | null;
  open: boolean;
  currentTemplateIds?: string[];
  formatType?: string;
  onClose?: () => void;
}

const TemplateSelectDialog = ({ projectType, open, currentTemplateIds, formatType, onClose }: TemplateSelectDialogProps) => {
  const { projectId } = useProjectDetailContext();
  const queryClient = useQueryClient();
  const { data: templates = [] } = useTemplates();
  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes = [] } = useAdminTemplateFormatTypes();
  const { data: activeProjectTemplates = [] } = useActiveProjectTemplates(projectId, formatType);
  const resolvedTypeId = templateTypes.find((t) => t.name.toUpperCase() === projectType?.toUpperCase())?.id ?? null;
  const extractionFormatTypeId = formatTypes.find((ft) => ft.name === "Extraction")?.id ?? null;
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const prevOpenRef = useRef(false);

  const filteredTemplates = templates.filter((t) =>
    t.is_active &&
    (resolvedTypeId == null || t.template_type_id === resolvedTypeId) &&
    (extractionFormatTypeId == null || t.template_formatetype_ids?.includes(extractionFormatTypeId))
  );

  const activeSourceIds = new Set(filteredTemplates.map((t) => t.id));
  const orphanedTemplates = activeProjectTemplates
    .filter((pt) => !activeSourceIds.has(pt.source_template_id))
    .map((pt) => ({ id: pt.source_template_id, name: pt.name, description: pt.description, isOrphaned: true as const }));

  const displayTemplates = [
    ...filteredTemplates.map((t) => ({ ...t, isOrphaned: false as const })),
    ...orphanedTemplates,
  ];

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const preSelected = activeProjectTemplates.map((t) => t.source_template_id);
      setSelectedTemplateIds(new Set(preSelected));
      setSaveError(null);
    }
    prevOpenRef.current = open;
  }, [open, activeProjectTemplates]);

  const toggleTemplate = (id: string) => {
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedTemplateIds.size === 0) return;
    setIsSaving(true);
    setSaveError(null);
    const previousNames = activeProjectTemplates.map((t) => t.name);
    const newNames = displayTemplates.filter((t) => selectedTemplateIds.has(t.id)).map((t) => t.name);
    try {
      await projectsApi.bulkCreateProjectTemplates(projectId, [...selectedTemplateIds], formatType);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["projects", projectId, "template"] }),
        queryClient.invalidateQueries({ queryKey: ["projects", projectId, "templates", "active"] }),
        queryClient.invalidateQueries({ queryKey: ["projects", projectId, "templates", "active-with-prompts"] }),
      ]);
      void auditLogApi.recordAuditEvent({
        projectId,
        entityType: "project_template",
        entityId: projectId,
        entityName: formatType ?? "Template",
        action: "change_template",
        stage: "Extraction",
        before: JSON.stringify({ templates: previousNames }),
        after: JSON.stringify({ templates: newNames }),
      });
      onClose?.();
    } catch (err) {
      console.error("Failed to save templates:", err);
      setSaveError("Failed to save templates. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const canCancel = !!onClose && (currentTemplateIds?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg [&>button:last-child]:hidden flex flex-col max-h-[80vh]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Select Templates</DialogTitle>
        </DialogHeader>
        <Label className="font-semibold shrink-0">Templates</Label>
        {displayTemplates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6 border">
            No templates available for this project type.
          </p>
        ) : (
          <ul role="listbox" aria-label="Select templates" aria-multiselectable="true" className="overflow-y-auto space-y-2 min-h-0">
            {displayTemplates.map((t) => {
              const checked = selectedTemplateIds.has(t.id);
              return (
                <li
                  key={t.id}
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggleTemplate(t.id)}
                  className={`border-2 px-4 py-3 cursor-pointer text-sm select-none transition-colors ${
                    checked ? "border-brand-teal" : "border-border hover:border-brand-teal"
                  }`}
                >
                  <div className={checked ? "font-medium" : ""}>
                    {t.name}
                    {t.isOrphaned && <span className="text-xs text-muted-foreground ml-2 font-normal">(Deactivated)</span>}
                  </div>
                  {t.description && <div className="text-muted-foreground">{t.description}</div>}
                </li>
              );
            })}
          </ul>
        )}
        {saveError && <p className="shrink-0 text-sm text-destructive">{saveError}</p>}
        <DialogFooter className="shrink-0">
          {canCancel && (
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
          )}
          <Button
            className="bg-brand-teal text-white hover:bg-brand-teal/90"
            disabled={isSaving || selectedTemplateIds.size === 0}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectDialog;
